import axiosInstance from 'integration/api/axiosInstance';

import {
  CommonEntitiesListResponse,
  AppointmentTypeResponseDto,
  ProfessionalAgendaResponseDto,
  AppointmentTypeCreationDto,
  AppointmentTypeUpdateDto,
  CustomAppointmentTypeNotificationsUpdateDto,
  AppointmentTypeNotificationConfigRequestDto,
} from '@docbay/schemas';

interface Props {
  page?: number;
  limit?: number;
  specializationIds?: string;
  clinicId?: string;
  professionalIds?: string;
  onlyProfessional?: boolean;
}

/**
 * An API service for appointment types.
 */
export const AppointmentTypesApi = {
  /**
   * Fetches a list of appointment types from the server.
   *
   * @param {Props} props - The parameters to send with the request.
   * @returns {Promise<CommonEntitiesListResponse<AppointmentTypeResponseDto>>} A promise that resolves to the response from the server.
   */
  fetchAppointmentTypes: ({
    page,
    limit,
    clinicId,
    specializationIds,
    professionalIds,
    onlyProfessional,
  }: Props) => {
    const params = {
      includeDeleted: 'false',
      include: 'clinic,professionals,specialization,notificationConfig',
      ...(clinicId ? { clinicId } : {}),
      ...(specializationIds ? { specializationIds } : {}),
      ...(professionalIds ? { professionalIds } : {}),
      ...(page ? { page } : {}),
      ...(limit ? { limit } : {}),
      ...(onlyProfessional ? { onlyProfessional } : {}),
    };

    return axiosInstance.get<
      CommonEntitiesListResponse<AppointmentTypeResponseDto>
    >('/appointment-type', {
      params,
    });
  },

  /**
   * Creates a new appointment type on the server.
   *
   * @param {AppointmentTypeCreationDto} data - The data for the appointment type to create.
   * @returns {Promise<ProfessionalAgendaResponseDto[]>} A promise that resolves to the response from the server.
   */
  postAppointmentType: (data: AppointmentTypeCreationDto) =>
    axiosInstance.post<ProfessionalAgendaResponseDto[]>(
      '/appointment-type',
      data,
    ),

  /**
   * Fetches a single appointment type by its ID from the server.
   *
   * @param {string} id - The ID of the appointment type to fetch.
   * @returns {Promise<AppointmentTypeResponseDto>} A promise that resolves to the response from the server.
   */
  fetchAppointmentTypeById: (id: string) => {
    return axiosInstance.get<AppointmentTypeResponseDto>(
      `/appointment-type/${id}?include=clinic,professionals,specialization,notifications,customNotifications`,
    );
  },

  /**
   * Updates an existing appointment type on the server.
   *
   * @param {string} id - The ID of the appointment type to update.
   * @param {AppointmentTypeUpdateDto} data - The data for the appointment type to update.
   * @returns {Promise<void>} A promise that resolves when the update is complete.
   */
  updateAppointmentType: (id: string, data: AppointmentTypeUpdateDto) => {
    return axiosInstance.patch(`/appointment-type/${id}`, data);
  },

  /**
   * Deletes an existing appointment type from the server by its ID.
   *
   * @param {string} id - The ID of the appointment type to delete.
   * @returns {Promise<void>} A promise that resolves when the deletion is complete.
   */
  deleteAppointmentType: (id: string) =>
    axiosInstance.delete(`/appointment-type/${id}`),

  /**
   * Updates the custom notifications for an existing appointment type on the server.
   *
   * @param {string} id - The ID of the appointment type to update.
   * @param {CustomAppointmentTypeNotificationsUpdateDto} data - The data for the custom notifications to update.
   * @returns {Promise<void>} A promise that resolves when the update is complete.
   */
  updateAppointmentTypeNotifications: (
    id: string,
    data: CustomAppointmentTypeNotificationsUpdateDto,
  ) =>
    axiosInstance.patch(
      `/appointment-type/custom-part-of-notifications/${id}`,
      data,
    ),

  /**
   * Changes the notification configuration for an existing appointment type on the server.
   *
   * @param {string} id - The ID of the appointment type to update.
   * @param {AppointmentTypeNotificationConfigRequestDto} data - The data for the notification configuration to update.
   * @returns {Promise<void>} A promise that resolves when the update is complete.
   */
  changeAppointmentTypeNotificationsConfig: (
    id: string,
    data: AppointmentTypeNotificationConfigRequestDto,
  ) =>
    axiosInstance.patch(`/appointment-type/notifications-config/${id}`, data),
};
